import type { User } from "firebase/auth";
import {
	GoogleAuthProvider,
	browserLocalPersistence,
	browserPopupRedirectResolver,
	createUserWithEmailAndPassword,
	initializeAuth,
	sendPasswordResetEmail,
	signInWithCustomToken,
	signInWithEmailAndPassword,
	signInWithRedirect,
} from "firebase/auth";
import { Feedback, Feedbacks, NONE, Store, isObject } from "shelving";
import { FIREBASE_APP } from "./app.js";

// Init.
export const FIREBASE_AUTH = initializeAuth(FIREBASE_APP, {
	persistence: browserLocalPersistence,
	popupRedirectResolver: browserPopupRedirectResolver,
});
FIREBASE_AUTH.languageCode = "en";

/** Store the currently active Firebase Auth user. */
export const AUTH = new Store<User | null>(NONE);
FIREBASE_AUTH.onAuthStateChanged(user => AUTH.set(user || null));

/**
 * Parse an error thrown by an auth call and return an appropriate friendly Invalid instance.
 * - This is so forms etc show a friendly message when errors happen.
 * - The error will be attached to the correct field too.
 * - Only uses a subset of errors from: https://firebase.google.com/docs/auth/admin/errors
 */
function _errorToFeedback(err: unknown): void {
	if (isObject(err)) {
		const code = err.code;
		if (typeof code === "string") {
			if (code === "auth/network-request-failed") throw new Feedback("No internet connection");
			if (code === "auth/user-not-found") throw new Feedbacks({ email: new Feedback("Unknown user") });
			if (code === "auth/user-disabled") throw new Feedbacks({ email: new Feedback("Account is disabled") });
			if (code === "auth/wrong-password") throw new Feedbacks({ password: new Feedback("Incorrect password") });
		}
	}
}

/** Login with a username. */
export async function loginWithEmail({ email, password }: { email: string; password: string }): Promise<string> {
	try {
		const credential = await signInWithEmailAndPassword(FIREBASE_AUTH, email, password);
		return credential.user.uid;
	} catch (err) {
		_errorToFeedback(err);
		throw err;
	}
}

/** Login with a custom token (e.g. as returned from a Cloud Function). */
export async function loginWithToken(token: string): Promise<string> {
	try {
		const credential = await signInWithCustomToken(FIREBASE_AUTH, token);
		return credential.user.uid;
	} catch (err) {
		_errorToFeedback(err);
		throw err;
	}
}

/** Login with a popup using Google as an external provider. */
export async function loginWithGoogle(): Promise<void> {
	await signInWithRedirect(FIREBASE_AUTH, new GoogleAuthProvider());
}

/** Login with a username. */
export async function resetPassword({ email }: { email: string }): Promise<true> {
	try {
		await sendPasswordResetEmail(FIREBASE_AUTH, email);
		return true;
	} catch (err) {
		_errorToFeedback(err);
		throw err;
	}
}

/** Register a new account in Firebase auth. */
export async function register({ email, password }: { username: string; email: string; password: string }): Promise<string> {
	try {
		const credential = await createUserWithEmailAndPassword(FIREBASE_AUTH, email, password);
		return credential.user.uid;
	} catch (err) {
		_errorToFeedback(err);
		throw err;
	}
}

/** Log out by clearing any local persistence. */
export async function logout(): Promise<void> {
	await FIREBASE_AUTH.signOut();
}
